import { Routes, Route, Outlet } from 'react-router-dom';
import { lazy, Suspense, useEffect } from 'react';
import { AuthGuard } from './components';
import { useAppDispatch } from './store/hooks';
import { fetchCart } from './store/cartSlice';
import { setStorefrontApiPrefix } from './services/api';
import ConsentBanner from './components/ConsentBanner';
// B98 — the cookie banner the published Cookie Policy promises (not the POPIA one above).
import CookieConsentBanner from './components/CookieConsentBanner';
// Not lazy: the hostname split has to be decided before anything paints.
import DomainRouter from './components/DomainRouter';
// Not lazy either: the head has to be right in the first paint a crawler sees.
import SeoManager from './components/SeoManager';
import ScrollToTop from './components/ScrollToTop';

// Lazy load pages for code splitting
const LoginPage = lazy(() => import('./pages/LoginPage'));
const TwoFactorChallengePage = lazy(() => import('./pages/TwoFactorChallengePage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const StaffAcceptInvitePage = lazy(() => import('./pages/StaffAcceptInvitePage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const AccountSuspendedPage = lazy(() => import('./pages/AccountSuspendedPage'));
const UnauthorizedPage = lazy(() => import('./pages/UnauthorizedPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const BillingPaymentPage = lazy(() => import('./pages/BillingPaymentPage'));
const TemplatesPage = lazy(() => import('./pages/TemplatesPage'));
const SupportTicketsPage = lazy(() => import('./pages/SupportTicketsPage'));
const UsersPage = lazy(() => import('./pages/UsersPage'));
const UserManagementPage = lazy(() => import('./pages/UserManagementPage'));
const CrmContactsPage = lazy(() => import('./pages/CrmContactsPage'));
const CrmDealsPage = lazy(() => import('./pages/CrmDealsPage'));
const CrmAutomationsPage = lazy(() => import('./pages/CrmAutomationsPage'));
const CrmSequencesPage = lazy(() => import('./pages/CrmSequencesPage'));
const MerchantStaffPage = lazy(() => import('./pages/MerchantStaffPage'));
const NewsletterPage = lazy(() => import('./pages/NewsletterPage'));
const AffiliateManagePage = lazy(() => import('./pages/AffiliateManagePage'));
const AffiliatePage = lazy(() => import('./pages/AffiliatePage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const KbAdminPage = lazy(() => import('./pages/KbAdminPage'));
const ConnectorRunbookPage = lazy(() => import('./pages/ConnectorRunbookPage'));
const MerchantGuidesPage = lazy(() => import('./pages/MerchantGuidesPage'));
const KnowledgeBasePage = lazy(() => import('./pages/KnowledgeBasePage'));
const AnnouncementsAdminPage = lazy(() => import('./pages/AnnouncementsAdminPage'));
const AnnouncementsPage = lazy(() => import('./pages/AnnouncementsPage'));
const StatusPage = lazy(() => import('./pages/StatusPage'));
// M3's three standalone public pages. They are MARKETING paths, so each one
// also needs an entry in config/domains MARKETING_PREFIXES and in config/seo
// (PageSeo + SITEMAP_PATHS) — a route added here alone compiles and then
// redirects every recurr.co.za visitor to the login screen.
const AboutPage = lazy(() => import('./pages/AboutPage'));
const SecurityPage = lazy(() => import('./pages/SecurityPage'));
const DevelopersPage = lazy(() => import('./pages/DevelopersPage'));
const PluginComparisonPage = lazy(() => import('./pages/PluginComparisonPage'));
const PlatformAccountPage = lazy(() => import('./pages/PlatformAccountPage'));
const BlogIndexPage = lazy(() => import('./pages/BlogIndexPage'));
const BlogArticlePage = lazy(() => import('./pages/BlogArticlePage'));
const MyDocumentsPage = lazy(() => import('./pages/MyDocumentsPage'));
const AdminKycQueuePage = lazy(() => import('./pages/admin/AdminKycQueuePage'));
const MerchantPoliciesPage = lazy(() => import('./pages/MerchantPoliciesPage'));
const PolicyViewPage = lazy(() => import('./pages/PolicyViewPage'));
const LegalHubPage = lazy(() => import('./pages/LegalHubPage'));
const AdminUserManagementPage = lazy(() => import('./pages/AdminUserManagementPage'));
const AdminStaffPage = lazy(() => import('./pages/AdminStaffPage'));
const AdminBillingPage        = lazy(() => import('./pages/admin/AdminBillingPage'));
const AdminAuditLogsPage      = lazy(() => import('./pages/admin/AdminAuditLogsPage'));
const AdminJobsPage           = lazy(() => import('./pages/admin/AdminJobsPage'));
const AdminEmailPreviewPage   = lazy(() => import('./pages/admin/AdminEmailPreviewPage'));
const PayFastSettingsPage = lazy(() => import('./pages/PayFastSettingsPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const TwoFactorSetupPage = lazy(() => import('./pages/TwoFactorSetupPage'));
const ApiKeysPage = lazy(() => import('./pages/ApiKeysPage'));
const MerchantWebhooksPage = lazy(() => import('./pages/MerchantWebhooksPage'));
const MerchantWebhookDeliveriesPage = lazy(() => import('./pages/MerchantWebhookDeliveriesPage'));
const AdminWebhookDeliveriesPage = lazy(() => import('./pages/admin/AdminWebhookDeliveriesPage'));
const AdminEmailFailuresPage     = lazy(() => import('./pages/admin/AdminEmailFailuresPage'));  // B65
const AdminWaitlistPage          = lazy(() => import('./pages/admin/AdminWaitlistPage'));  // M4
const DesignSystemDemoPage = lazy(() => import('./pages/_dev/DesignSystemDemoPage'));
const ProductManagementPage = lazy(() => import('./pages/ProductManagementPage'));
const RecurrPlansPage = lazy(() => import('./pages/RecurrPlansPage'));
const MerchantPayFastSettingsPage = lazy(() => import('./pages/MerchantPayFastSettingsPage'));
const SubscriptionsPage           = lazy(() => import('./pages/SubscriptionsPage'));
const ManualSubscriptionPage      = lazy(() => import('./pages/ManualSubscriptionPage'));
const DunningDashboardPage        = lazy(() => import('./pages/DunningDashboardPage'));
const CreditHistoryPage           = lazy(() => import('./pages/CreditHistoryPage'));
const InvoiceManagementPage       = lazy(() => import('./pages/InvoiceManagementPage'));
const InvoiceDetailPage           = lazy(() => import('./pages/InvoiceDetailPage'));
const AccountingExportPage        = lazy(() => import('./pages/AccountingExportPage'));
const CustomerInvoicesPage          = lazy(() => import('./pages/CustomerInvoicesPage'));
const OrdersPage                  = lazy(() => import('./pages/OrdersPage'));
const QuotesPage                  = lazy(() => import('./pages/QuotesPage'));
const FraudDenyListPage           = lazy(() => import('./pages/FraudDenyListPage'));
const MerchantBrandingPage        = lazy(() => import('./pages/MerchantBrandingPage'));
const PagesListPage               = lazy(() => import('./pages/PagesListPage'));
const PageBuilderPage             = lazy(() => import('./pages/PageBuilderPage'));
const MediaLibraryPage            = lazy(() => import('./pages/MediaLibraryPage'));
const TransactionsPage            = lazy(() => import('./pages/TransactionsPage'));
const StatementsPage              = lazy(() => import('./pages/StatementsPage'));
const CustomerBillingPage           = lazy(() => import('./pages/CustomerBillingPage'));
const ReportsPage                 = lazy(() => import('./pages/ReportsPage'));
const CommunicationTemplatesPage  = lazy(() => import('./pages/CommunicationTemplatesPage'));
const TemplateEditorPage          = lazy(() => import('./pages/TemplateEditorPage'));
const CouponsPage                 = lazy(() => import('./pages/CouponsPage'));
const CustomerDetailPage            = lazy(() => import('./pages/CustomerDetailPage'));
const BusinessSettingsPage        = lazy(() => import('./pages/BusinessSettingsPage'));
const NotificationPreferencesPage = lazy(() => import('./pages/NotificationPreferencesPage'));
const MerchantSettingsPage        = lazy(() => import('./pages/MerchantSettingsPage'));
const OnboardingWizardPage        = lazy(() => import('./pages/OnboardingWizardPage'));
const SubscribeLinksPage          = lazy(() => import('./pages/SubscribeLinksPage'));
const PrivacySettingsPage         = lazy(() => import('./pages/PrivacySettingsPage'));
const BillingRunHistoryPage       = lazy(() => import('./pages/BillingRunHistoryPage'));
const LowCreditWarningsPage       = lazy(() => import('./pages/LowCreditWarningsPage'));
const SMSManagementPage           = lazy(() => import('./pages/merchant/SMSManagementPage'));

// Public pages — no auth required
const MerchantSignupPage             = lazy(() => import('./pages/MerchantSignupPage'));
const MerchantSignupConfirmationPage = lazy(() => import('./pages/MerchantSignupConfirmationPage'));
// Wave 1.2.1 — standalone signup surfaces (no plan / no purchase)
const MerchantStandaloneSignupPage   = lazy(() => import('./pages/MerchantStandaloneSignupPage'));
const CustomerStandaloneSignupPage     = lazy(() => import('./pages/CustomerStandaloneSignupPage'));
const EmailVerificationPage          = lazy(() => import('./pages/EmailVerificationPage'));
const LandingPage                    = lazy(() => import('./pages/LandingPage'));
const HomePage                       = lazy(() => import('./pages/HomePage'));

// Public store pages (no auth required)
const PublicPageView        = lazy(() => import('./pages/public/PublicPageView'));
const CartPage              = lazy(() => import('./pages/public/CartPage'));
const CheckoutPage          = lazy(() => import('./pages/public/CheckoutPage'));
const OrderConfirmationPage = lazy(() => import('./pages/public/OrderConfirmationPage'));
const CheckoutReturnRedirect = lazy(() => import('./pages/public/CheckoutReturnRedirect'));
const CheckoutCancelRedirect = lazy(() => import('./pages/public/CheckoutCancelRedirect'));
// R4a §6.7 chunk 3 — the anonymous page a subscribe link opens.
const HostedSubscribePage   = lazy(() => import('./pages/public/HostedSubscribePage'));
const PublicInvoicePayPage  = lazy(() => import('./pages/public/PublicInvoicePayPage'));
const PublicQuoteAcceptPage = lazy(() => import('./pages/public/PublicQuoteAcceptPage'));
// R1 §12.8 — the no-login pay-to-start page the free-window reminder emails
// link to. Public by construction: its reader is a merchant who is not signed
// in, which is the reason the reminder is an email rather than a banner.
const StartPlanPage         = lazy(() => import('./pages/public/StartPlanPage'));

// In-dashboard Store (Wave 4.7a — authenticated)
const StoreDashboardPage    = lazy(() => import('./pages/StoreDashboardPage'));
// In-dashboard plan picker (Wave 4.7b OD-2 — authenticated merchant)
const PlanSelectPage        = lazy(() => import('./pages/PlanSelectPage'));

// Loading component
const LoadingFallback = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    fontSize: '1.2rem',
    color: '#666'
  }}>
    Loading...
  </div>
);

// Wave 4.7a — in-dashboard Store boundary: points the cart/checkout API at the
// authenticated `/store/*` mirror routes for the whole subtree, and clears it
// on exit so the public storefront keeps using its hostname-resolved routes.
const StoreDashboardBoundary = () => {
  // Set synchronously in render so child pages' effects (e.g. the confirmation
  // poll) see the '/store' prefix before they fire; reset on unmount.
  setStorefrontApiPrefix('/store');
  useEffect(() => () => setStorefrontApiPrefix(''), []);
  return <Outlet />;
};

function App() {
  const dispatch = useAppDispatch();

  // Restore an EXISTING cart from localStorage on startup. We do NOT create a
  // cart eagerly here — that fired POST /cart on every page (incl. marketing /
  // login) and 404'd on non-storefront hostnames. The cart is created lazily on
  // the first add (see addCartItemAsync); boot only re-hydrates a saved token.
  useEffect(() => {
    if (localStorage.getItem('recurr_cart_token')) {
      dispatch(fetchCart());
    }
  }, [dispatch]);

  return (
    <div className="app">
      {/* Marketing (recurr.co.za) vs platform (app.recurr.co.za) — see
          components/DomainRouter. A no-op on localhost, dev and every
          merchant domain. */}
      <DomainRouter>
      {/* Per-route <title>, description, canonical and share card. Before this
          every route served index.html's single set of tags, so /, /pricing
          and /legal/* were one search result between them. */}
      <SeoManager />
      {/* A new page opens at its top — see components/ScrollToTop. */}
      <ScrollToTop />
      <ConsentBanner />
      <CookieConsentBanner />
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/get-started" element={<LandingPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/login/2fa" element={<TwoFactorChallengePage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
          <Route path="/start-plan/:token" element={<StartPlanPage />} />
          <Route path="/staff/accept/:token" element={<StaffAcceptInvitePage />} />
          <Route path="/unauthorized" element={<UnauthorizedPage />} />
          {/* Recovery surface for suspended / overdue merchants (Wave 1.1 Step 5b).
              Not wrapped in AuthGuard — the 423 interceptor brings users here
              with their token still valid; the page itself reads /me to verify
              identity and redirects to /dashboard if not actually suspended. */}
          <Route path="/account-suspended" element={<AccountSuspendedPage />} />
        <Route
          path="/dashboard"
          element={
            <AuthGuard>
              <DashboardPage />
            </AuthGuard>
          }
        />
        <Route
          path="/plans"
          element={
            <AuthGuard requiredRole="merchant">
              <PlanSelectPage />
            </AuthGuard>
          }
        />
        <Route
          path="/billing"
          element={
            <AuthGuard>
              <BillingPaymentPage />
            </AuthGuard>
          }
        />
        <Route
          path="/templates"
          element={
            <AuthGuard>
              <TemplatesPage />
            </AuthGuard>
          }
        />
        <Route
          path="/support"
          element={
            <AuthGuard>
              <SupportTicketsPage />
            </AuthGuard>
          }
        />
        <Route
          path="/users"
          element={
            <AuthGuard>
              <UsersPage />
            </AuthGuard>
          }
        />
        <Route
          path="/user-management"
          element={
            <AuthGuard>
              <UserManagementPage />
            </AuthGuard>
          }
        />
        <Route
          path="/crm"
          element={
            <AuthGuard requiredRole="merchant" staffPermission="crm.view">
              <CrmContactsPage />
            </AuthGuard>
          }
        />
        <Route
          path="/crm/deals"
          element={
            <AuthGuard requiredRole="merchant" staffPermission="crm.view">
              <CrmDealsPage />
            </AuthGuard>
          }
        />
        <Route
          path="/crm/automations"
          element={
            <AuthGuard requiredRole="merchant" staffPermission="crm.view">
              <CrmAutomationsPage />
            </AuthGuard>
          }
        />
        <Route
          path="/crm/sequences"
          element={
            <AuthGuard requiredRole="merchant" staffPermission="crm.view">
              <CrmSequencesPage />
            </AuthGuard>
          }
        />
        <Route
          path="/merchant/staff"
          element={
            <AuthGuard requiredRole="merchant">
              <MerchantStaffPage />
            </AuthGuard>
          }
        />
        <Route
          path="/newsletter"
          element={
            <AuthGuard>
              <NewsletterPage />
            </AuthGuard>
          }
        />
        <Route
          path="/my-documents"
          element={
            <AuthGuard>
              <MyDocumentsPage />
            </AuthGuard>
          }
        />
        <Route
          path="/admin/kyc"
          element={
            <AuthGuard requiredRole="admin" staffPermission="kyc.review">
              <AdminKycQueuePage />
            </AuthGuard>
          }
        />
        <Route
          path="/merchant/policies-legal"
          element={
            <AuthGuard requiredRole="merchant">
              <MerchantPoliciesPage />
            </AuthGuard>
          }
        />
        {/* Public — legal hub + policy viewer (storefront footers, signup links) */}
        <Route path="/legal" element={<LegalHubPage />} />
        <Route path="/legal/:docType" element={<PolicyViewPage />} />
        {/* Public — network status board (marketing header/footer links here) */}
        <Route path="/status" element={<StatusPage />} />
        {/* Public — M3's three pages. Linked from the marketing FOOTER and from
            the home page's body copy, never the top nav: the header row was
            measured at 920px with six links and one CTA, and three more items
            would need a wider breakpoint than the row that measurement
            replaced. Reasoning in MarketingPageShell.tsx. */}
        <Route path="/about" element={<AboutPage />} />
        <Route path="/security" element={<SecurityPage />} />
        <Route path="/developers" element={<DevelopersPage />} />
        {/* Public — A10's plugin-or-platform comparison (2026-09-14), linked
            from the home page's banner above "Open books" and from /about.
            Not from the footer, by the founder's ruling — see PluginComparisonPage. */}
        <Route path="/wordpress-plugin" element={<PluginComparisonPage />} />
        {/* Public — B131 (2026-10-25): the platform account, announced before it
            is sellable. Linked from the block under the plan cards on / and
            /pricing; not from the header or the footer. */}
        <Route path="/platform-account" element={<PlatformAccountPage />} />
        {/* Public — the blog (C1 / M5 part 1, 2026-10-10). The index is linked
            from the top nav ("Blog", the founder's instruction); each article's
            head and sitemap line are derived from config/blog.ts. An unknown
            slug renders the 404 page, with its noindex. */}
        <Route path="/blog" element={<BlogIndexPage />} />
        <Route path="/blog/:slug" element={<BlogArticlePage />} />
        <Route
          path="/affiliate"
          element={
            <AuthGuard requiredRole="merchant">
              <AffiliatePage />
            </AuthGuard>
          }
        />
        <Route
          path="/affiliate/manage"
          element={
            <AuthGuard requiredRole="merchant">
              <AffiliateManagePage />
            </AuthGuard>
          }
        />
        <Route
          path="/kb"
          element={
            <AuthGuard requiredRole="merchant">
              <KbAdminPage />
            </AuthGuard>
          }
        />
        {/*
          The WooCommerce cut-over runbook. Reached from the connector download
          card on the API-keys page — a real URL rather than a modal, because a
          merchant sends this one to whoever administers their WordPress site
          and works through it with the page open beside them.

          Guarded by sign-in only, like the API-keys page it is reached from:
          the backend route is merchant-or-admin, and the runbook is written
          for "the merchant, or ReCurr support sitting beside them" — so
          support must be able to open the same URL the merchant is reading.
        */}
        <Route
          path="/merchant/woocommerce-runbook"
          element={
            <AuthGuard>
              <ConnectorRunbookPage />
            </AuthGuard>
          }
        />
        {/*
          Help → Guides (A5). Sign-in only, like the runbook above: the backend
          route is merchant-or-admin, so ReCurr support can open the same guide
          a merchant is reading.
        */}
        <Route
          path="/help/guides"
          element={
            <AuthGuard>
              <MerchantGuidesPage />
            </AuthGuard>
          }
        />
        <Route
          path="/help/guides/:slug"
          element={
            <AuthGuard>
              <MerchantGuidesPage />
            </AuthGuard>
          }
        />
        <Route
          path="/admin/kb"
          element={
            <AuthGuard requiredRole="admin">
              <KbAdminPage />
            </AuthGuard>
          }
        />
        {/* Announcements — feed (all roles) + authoring (merchant / admin) */}
        <Route
          path="/announcements"
          element={
            <AuthGuard>
              <AnnouncementsPage />
            </AuthGuard>
          }
        />
        <Route
          path="/announcements/manage"
          element={
            <AuthGuard requiredRole="merchant">
              <AnnouncementsAdminPage />
            </AuthGuard>
          }
        />
        <Route
          path="/admin/announcements"
          element={
            <AuthGuard requiredRole="admin">
              <AnnouncementsAdminPage platform />
            </AuthGuard>
          }
        />
        <Route
          path="/customers/:customerId"
          element={
            <AuthGuard>
              <CustomerDetailPage />
            </AuthGuard>
          }
        />
        <Route
          path="/admin/users"
          element={
            <AuthGuard>
              <AdminUserManagementPage />
            </AuthGuard>
          }
        />
        <Route
          path="/admin/staff"
          element={
            <AuthGuard requiredRole="admin">
              <AdminStaffPage />
            </AuthGuard>
          }
        />
        <Route
          path="/admin/billing"
          element={
            <AuthGuard>
              <AdminBillingPage />
            </AuthGuard>
          }
        />
        <Route
          path="/settings/payfast"
          element={
            <AuthGuard>
              <PayFastSettingsPage />
            </AuthGuard>
          }
        />
        <Route
          path="/settings/payment-gateway"
          element={
            <AuthGuard>
              <MerchantPayFastSettingsPage />
            </AuthGuard>
          }
        />
        <Route
          path="/profile"
          element={
            <AuthGuard>
              <ProfilePage />
            </AuthGuard>
          }
        />
        <Route
          path="/profile/2fa/setup"
          element={
            <AuthGuard>
              <TwoFactorSetupPage />
            </AuthGuard>
          }
        />
        <Route
          path="/merchant/api-keys"
          element={
            <AuthGuard>
              <ApiKeysPage />
            </AuthGuard>
          }
        />
        <Route
          path="/merchant/webhooks"
          element={
            <AuthGuard>
              <MerchantWebhooksPage />
            </AuthGuard>
          }
        />
        <Route
          path="/merchant/webhooks/:id/deliveries"
          element={
            <AuthGuard>
              <MerchantWebhookDeliveriesPage />
            </AuthGuard>
          }
        />
        <Route
          path="/admin/webhook-deliveries"
          element={
            <AuthGuard>
              <AdminWebhookDeliveriesPage />
            </AuthGuard>
          }
        />
        {/* Wave 4.1.A — design-system reference page. Behind auth so only
            logged-in devs / founders can access it; not linked from nav. */}
        <Route
          path="/_dev/design-system"
          element={
            <AuthGuard>
              <DesignSystemDemoPage />
            </AuthGuard>
          }
        />
        <Route
          path="/products"
          element={
            <AuthGuard>
              <ProductManagementPage />
            </AuthGuard>
          }
        />
        <Route
          path="/admin/merchant-plans"
          element={
            <AuthGuard>
              <RecurrPlansPage />
            </AuthGuard>
          }
        />
        <Route
          path="/admin/audit-logs"
          element={
            <AuthGuard>
              <AdminAuditLogsPage />
            </AuthGuard>
          }
        />
        <Route
          path="/admin/jobs"
          element={
            <AuthGuard>
              <AdminJobsPage />
            </AuthGuard>
          }
        />
        {/* B65 — every email the provider refused, platform-wide. requiredRole="admin"
            matches the endpoint: the list carries every person's address. */}
        <Route
          path="/admin/email-failures"
          element={
            <AuthGuard requiredRole="admin">
              <AdminEmailFailuresPage />
            </AuthGuard>
          }
        />
        <Route
          path="/admin/email-preview"
          element={
            <AuthGuard>
              <AdminEmailPreviewPage />
            </AuthGuard>
          }
        />
        {/* M4 — who asked to be told when a tier opens. requiredRole="admin"
            matches the endpoint behind it: every row is a named individual's
            address, given under a specific consent, and that is the platform
            owner's picture of his own demand rather than staff-desk data. */}
        <Route
          path="/admin/waitlist"
          element={
            <AuthGuard requiredRole="admin">
              <AdminWaitlistPage />
            </AuthGuard>
          }
        />
        <Route
          path="/subscriptions"
          element={
            <AuthGuard>
              <SubscriptionsPage />
            </AuthGuard>
          }
        />
        <Route
          path="/billing/manual-subscriptions"
          element={
            <AuthGuard>
              <ManualSubscriptionPage />
            </AuthGuard>
          }
        />
        <Route
          path="/billing/dunning"
          element={
            <AuthGuard>
              <DunningDashboardPage />
            </AuthGuard>
          }
        />
        <Route
          path="/billing/credit-history"
          element={
            <AuthGuard>
              <CreditHistoryPage />
            </AuthGuard>
          }
        />
        <Route
          path="/billing/runs"
          element={
            <AuthGuard>
              <BillingRunHistoryPage />
            </AuthGuard>
          }
        />
        <Route
          path="/billing/low-credit-warnings"
          element={
            <AuthGuard>
              <LowCreditWarningsPage />
            </AuthGuard>
          }
        />
        <Route
          path="/billing/credit-history/:userId"
          element={
            <AuthGuard>
              <CreditHistoryPage />
            </AuthGuard>
          }
        />
        <Route
          path="/merchant/sms"
          element={
            <AuthGuard>
              <SMSManagementPage />
            </AuthGuard>
          }
        />
        <Route
          path="/invoices"
          element={
            <AuthGuard>
              <InvoiceManagementPage />
            </AuthGuard>
          }
        />
        {/* B141 — an invoice opened from the Invoices list (its own page). */}
        <Route
          path="/invoices/:id"
          element={
            <AuthGuard>
              <InvoiceDetailPage />
            </AuthGuard>
          }
        />
        <Route
          path="/billing/accounting-export"
          element={
            <AuthGuard>
              <AccountingExportPage />
            </AuthGuard>
          }
        />
        <Route
          path="/orders"
          element={
            <AuthGuard>
              <OrdersPage />
            </AuthGuard>
          }
        />
        <Route
          path="/quotes"
          element={
            <AuthGuard>
              <QuotesPage />
            </AuthGuard>
          }
        />
        <Route
          path="/fraud-deny-list"
          element={
            <AuthGuard>
              <FraudDenyListPage />
            </AuthGuard>
          }
        />
        <Route
          path="/my-invoices"
          element={
            <AuthGuard>
              <CustomerInvoicesPage />
            </AuthGuard>
          }
        />

        <Route
          path="/merchant/branding"
          element={
            <AuthGuard>
              <MerchantBrandingPage />
            </AuthGuard>
          }
        />

        <Route
          path="/settings/business"
          element={
            <AuthGuard>
              <BusinessSettingsPage />
            </AuthGuard>
          }
        />
        <Route
          path="/merchant-settings"
          element={
            <AuthGuard>
              <MerchantSettingsPage />
            </AuthGuard>
          }
        />
        <Route
          path="/onboarding-wizard"
          element={
            <AuthGuard>
              <OnboardingWizardPage />
            </AuthGuard>
          }
        />
        <Route
          path="/subscribe-links"
          element={
            <AuthGuard>
              <SubscribeLinksPage />
            </AuthGuard>
          }
        />
        <Route
          path="/settings/notifications"
          element={
            <AuthGuard>
              <NotificationPreferencesPage />
            </AuthGuard>
          }
        />
        <Route
          path="/settings/privacy"
          element={
            <AuthGuard>
              <PrivacySettingsPage />
            </AuthGuard>
          }
        />

        <Route
          path="/merchant/pages"
          element={
            <AuthGuard>
              <PagesListPage />
            </AuthGuard>
          }
        />
        <Route
          path="/merchant/pages/:id/edit"
          element={
            <AuthGuard>
              <PageBuilderPage />
            </AuthGuard>
          }
        />
        <Route
          path="/merchant/media"
          element={
            <AuthGuard>
              <MediaLibraryPage />
            </AuthGuard>
          }
        />

        <Route
          path="/transactions"
          element={
            <AuthGuard>
              <TransactionsPage />
            </AuthGuard>
          }
        />
        <Route
          path="/statements"
          element={
            <AuthGuard>
              <StatementsPage />
            </AuthGuard>
          }
        />
        <Route
          path="/my-account"
          element={
            <AuthGuard>
              <CustomerBillingPage />
            </AuthGuard>
          }
        />
        <Route
          path="/reports"
          element={
            <AuthGuard>
              <ReportsPage />
            </AuthGuard>
          }
        />
        <Route
          path="/communication-templates"
          element={
            <AuthGuard>
              <CommunicationTemplatesPage />
            </AuthGuard>
          }
        />
        <Route
          path="/communication-templates/:key"
          element={
            <AuthGuard>
              <TemplateEditorPage />
            </AuthGuard>
          }
        />
        <Route
          path="/coupons"
          element={
            <AuthGuard>
              <CouponsPage />
            </AuthGuard>
          }
        />

        {/* ------------------------------------------------------------------ */}
        {/* Public merchant signup routes — no authentication required         */}
        {/* ------------------------------------------------------------------ */}
        <Route path="/merchant/signup"                         element={<MerchantSignupPage />} />
        <Route path="/merchant/signup-free"                    element={<MerchantStandaloneSignupPage />} />
        <Route path="/merchant/signup/confirmation/:sessionId" element={<MerchantSignupConfirmationPage />} />

        {/* Wave 1.2.1 — non-blocking email verification (public landing) */}
        <Route path="/verify-email/:token"                     element={<EmailVerificationPage />} />

        {/* ------------------------------------------------------------------ */}
        {/* Public merchant page routes — no auth, serve by merchantId+slug  */}
        {/* ------------------------------------------------------------------ */}
        <Route path="/p/:merchantId/signup" element={<CustomerStandaloneSignupPage />} />
        <Route path="/p/:merchantId/kb"        element={<KnowledgeBasePage />} />
        <Route path="/p/:merchantId/kb/:slug"  element={<KnowledgeBasePage />} />
        <Route path="/p/:merchantId"        element={<PublicPageView isHomepage />} />
        <Route path="/p/:merchantId/:slug"  element={<PublicPageView />} />

        {/* ------------------------------------------------------------------ */}
        {/* R4a §6.7 — the hosted subscribe page. Anonymous, and deliberately  */}
        {/* NOT under /store: the merchant is resolved from the link TOKEN, so */}
        {/* this works for a merchant with no ReCurr storefront domain at all. */}
        {/* ------------------------------------------------------------------ */}
        <Route path="/subscribe/:token"          element={<HostedSubscribePage />} />
        <Route path="/subscribe/:token/complete" element={<HostedSubscribePage isReturn />} />

        {/* R4a chunk 4 — payment links. Same page, same token space, different */}
        {/* word: a link asking someone to settle a failed payment must not say */}
        {/* "subscribe", because they already did.                              */}
        <Route path="/pay/:token"          element={<HostedSubscribePage mode="settle" />} />
        <Route path="/pay/:token/complete" element={<HostedSubscribePage mode="settle" isReturn />} />

        {/* A11 — the signed pay link carried by every invoice email. Anonymous:  */}
        {/* the invoice, the merchant and the amount are all resolved from the    */}
        {/* TOKEN, so someone who has no ReCurr account can settle an invoice     */}
        {/* they were emailed. One route, not two: PayFast returns here with      */}
        {/* ?paid=1, the way /my-invoices already handles its own return.         */}
        <Route path="/invoice/:token"      element={<PublicInvoicePayPage />} />

        {/* A14 — the signed view-and-accept link carried by every quote      */}
        {/* email. Anonymous, exactly like the pay link above: the quote, the  */}
        {/* merchant and the line items are all resolved from the TOKEN, so a  */}
        {/* prospective customer with no ReCurr account can accept or decline  */}
        {/* without signing in to a platform they have never heard of.         */}
        {/* ⚠ The backend builds this path in `quoteAcceptToken.ts` and this   */}
        {/* route is what makes it resolve — neither build fails when the two  */}
        {/* disagree (B17 / BP-104), which is what spa-link-targets sweeps.    */}
        <Route path="/quote/:token"        element={<PublicQuoteAcceptPage />} />

        {/* ------------------------------------------------------------------ */}
        {/* In-dashboard Store (Wave 4.7a) — authenticated, merchant resolved  */}
        {/* from the logged-in user. Checkout/confirmation arrive in 4.7a-2.   */}
        {/* ------------------------------------------------------------------ */}
        <Route
          path="/store-dashboard"
          element={
            <AuthGuard>
              <StoreDashboardBoundary />
            </AuthGuard>
          }
        >
          <Route index element={<StoreDashboardPage />} />
          <Route path="cart"                  element={<CartPage />} />
          <Route path="checkout"              element={<CheckoutPage />} />
          <Route path="checkout/confirmation" element={<OrderConfirmationPage />} />
        </Route>

        {/* PayFast return target (Wave 4.7a-2) — forwards to the confirmation  */}
        {/* page on the storefront the customer checked out from.              */}
        <Route path="/checkout/:id/complete" element={<CheckoutReturnRedirect />} />

        {/* PayFast CANCEL target (B17 / BP-104) — the customer backed out AT   */}
        {/* the gateway. Until 2026-09-10 nothing matched this and they landed  */}
        {/* on the 404 page while the return above worked; measured live.       */}
        {/* ⚠ Declared AFTER its sibling on purpose. React Router v6 ranks by   */}
        {/* specificity rather than source order, so `/checkout/:id/complete`   */}
        {/* wins on its own — but the ordering is written the safe way round    */}
        {/* anyway, and `spa-link-targets.test.ts` asserts the return target is  */}
        {/* NOT swallowed by this route, because that failure is silent: the    */}
        {/* customer would be bounced back to checkout after PAYING.            */}
        <Route path="/checkout/:id" element={<CheckoutCancelRedirect />} />

        {/* ------------------------------------------------------------------ */}
        {/* ⚠⚠ THE PUBLIC STOREFRONT IS WITHDRAWN — item B18, ruled 2026-09-09. */}
        {/*                                                                    */}
        {/* There was a `/store/*` subtree here (products, cart, checkout,     */}
        {/* confirmation) whose API resolves a merchant by HOSTNAME only.      */}
        {/* C5 withdrew every hostname that could resolve on 2026-09-02, so    */}
        {/* every one of those API calls answered 404 STOREFRONT_NOT_FOUND.    */}
        {/*                                                                    */}
        {/* ⚠ IT WAS NOT UNREACHABLE, WHICH IS HOW IT SURVIVED. Measured in a  */}
        {/* real browser against production on 2026-09-09, /store/products     */}
        {/* returned 200 and rendered a complete branded storefront — nav,     */}
        {/* cart drawer, "Powered by ReCurr" footer — and then the words       */}
        {/* "Failed to load products. Please try again." A transient message   */}
        {/* for a permanent condition, inviting a retry that cannot succeed.   */}
        {/*                                                                    */}
        {/* ▶ AN UNMATCHED /store/… NOW RENDERS "Page not found", AND THAT IS  */}
        {/* THE HONEST ANSWER RATHER THAN A REDIRECT: the path carries no      */}
        {/* merchant reference, so nothing here can know whose shop was meant. */}
        {/* Guessing one would be worse than saying so.                        */}
        {/*                                                                    */}
        {/* ⚠ The PAGES themselves are NOT withdrawn — /p/:merchantId and      */}
        {/* /p/:merchantId/signup are live and measured (B19, B20). The        */}
        {/* AUTHENTICATED storefront below at /store-dashboard is untouched    */}
        {/* and is the storefront a signed-in customer uses.                   */}
        {/*                                                                    */}
        {/* ▶ WHEN THE ANONYMOUS CATALOGUE OPENS, IT COMES BACK PATH-SCOPED    */}
        {/* (/p/:merchantId/store/…), never hostname-scoped — a hostname is    */}
        {/* the white-label feature and that is a Plus/Pro teaser (§6 item 6). */}
        {/* ------------------------------------------------------------------ */}

        {/* Catch-all — MUST stay last. Without it an unmatched URL rendered a  */}
        {/* blank page, which reads as an outage rather than a wrong address.   */}
        <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
      </DomainRouter>
    </div>
  );
}

export default App;
